import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { makeStyles } from 'tss-react/mui';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Grid from '@mui/material/Grid';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import DialogContent from '@mui/material/DialogContent';
import CircularProgress from '@mui/material/CircularProgress';
import TextField from '@mui/material/TextField';
import { createCategory, updateCategory } from '../../../../slices/itemsSlice';

const useStyles = makeStyles()((theme) => ({

  closeButton: {
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: theme.palette.grey[500],
  },
  wrapper: {
    position: 'relative',
  },
  buttonProgress: {
    color: theme.palette.secondary.dark,
    position: 'absolute',
    top: '50%',
    left: '50%',
    marginTop: -12,
    marginLeft: -12,
  },
}
));

export default function AddCategoryModal({ display, handleClose, category }) {
  const [loading, setLoading] = useState(false);
  const [categoryProperties, setCategoryProperties] = useState({});
  const { classes } = useStyles();
  const { t } = useTranslation('categories');
  const dispatch = useDispatch();

  // Le même écran sert à créer et à modifier : `material-table` offrait une
  // édition en ligne, remplacée par la fenêtre que les membres utilisent déjà.
  // Deux motifs d'édition dans la même application, c'est un de trop.
  const isEditing = Boolean(category && category.id);

  useEffect(() => {
    setCategoryProperties(
      {
        name: {
          variant: 'outlined',
          required: true,
          fullWidth: true,
          id: 'name',
          label: t('name'),
          name: 'name',
          value: (category && category.name) || '',
        },
      },
    );
  }, [t, category]);

  const handleSubmit = () => {
    setLoading(true);
    const newCategory = Object.values(categoryProperties)
      .reduce((acc, elem) => {
        acc[elem.id] = elem.value;
        return acc;
      }, {});
    dispatch(isEditing
      ? updateCategory({ ...category, ...newCategory })
      : createCategory(newCategory));
    setLoading(false);
    handleClose();
  };

  const handleChange = (event) => {
    event.preventDefault();
    const { target } = event;
    const { value, name } = target;
    if (name) {
      setCategoryProperties({
        ...categoryProperties,
        [name]: {
          ...categoryProperties[name],
          value,
        },
      });
    }
  };

  const modalContent = (
    <>
      <DialogTitle className={classes.root}>
        <Typography type="h3" variant="h3">
          {isEditing ? t('edit_category.title') : t('add_category.title')}
        </Typography>
        <IconButton
          aria-label="close"
          className={classes.closeButton}
          onClick={handleClose}
          size="large"
        >
          <CloseIcon />
        </IconButton>

      </DialogTitle>
      <DialogContent dividers>
        <form
          className={classes.form}
          noValidate
          onSubmit={handleSubmit}
          onChange={handleChange}
          // onClick={handleChange}
        >
          <Grid container spacing={1}>

            {Object.values(categoryProperties)
              .map((property) => (
                <Grid item xs={12} key={property.id}>
                  <TextField {...property} />
                </Grid>
              ))}
          </Grid>
        </form>
      </DialogContent>
      <DialogActions>
        <div className={classes.wrapper}>
          <Button
            type="submit"
            disabled={loading}
            fullWidth
            variant="contained"
            color="primary"
            onClick={handleSubmit}
          >
            {t('add_category.save_button')}
          </Button>
          {loading && (
            <CircularProgress size={24} className={classes.buttonProgress} />
          )}
        </div>

      </DialogActions>
    </>
  );

  return (
    <Dialog
      open={display}
      onClose={handleClose}
      fullWidth
    >
      {modalContent}
    </Dialog>
  );
}

AddCategoryModal.propTypes = {
  display: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  // Absente à la création, présente à l'édition.
  category: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
  }),
};
